import React, { useEffect, useMemo, useState } from 'react';
import { Box, Flex, Spinner } from '@chakra-ui/react';
import { MainUserInfo } from './mainInfoUser';
import { UserDescription } from './userDescription';
import { EditColorThere } from './editColorThere';
import { EditAccountIcon } from './editAccountIcon';
import { InfoPane } from './infoPane';
import { useNativeBalance } from 'react-moralis';
import { ethers } from 'ethers';
import { useUserProvider } from '../../../providers/User/userProvider';
import { t } from 'i18next';
import { useBlockchainProvider } from 'src/providers/Blockchain/blockchainProvider';
import useFetchAirdrop from 'src/providers/Airdrop/useFetchAirdrop';
import Moralis from 'moralis-v1';
import { formatUsd, STRING_PLACEHOLDER, trimBalance } from '../../../utils/parser';

enum RESULT_TYPES {
	SUCCESS,
	NO_USER,
	LOADING,
}

export const UserHeader = ({ isOwner, id }: { isOwner: boolean; id: string }) => {
	const { data, isLoading } = useUserProvider();
	const { isEthereum, login, isLoggedIn, signer, account, provider } = useBlockchainProvider();

	const [balanceFormatted, setBalanceFormatted] = useState(STRING_PLACEHOLDER);
	const [hashBalanceFormatted, setHashBalanceFormatted] = useState(STRING_PLACEHOLDER);
	const airdropHashBalance = useFetchAirdrop(id);
	const [USDCBalanceFormatted, setUSDCBalanceFormatted] = useState(STRING_PLACEHOLDER);

	/** TODO: HashUp token */
	// const { fetchERC20Balances, data: erc20Data } = useERC20Balances();
	const erc20Data = null as any;

	const { getBalances, data: nativeBalance } = useNativeBalance();

	useEffect(() => {
		if (isLoading || id === ethers.constants.AddressZero) {
			return;
		}

		void getBalances({ params: { chain: 'polygon', address: id } });
	}, [isLoading, provider, account]);

	/** User USDC (erc20) balance; TODO: hash */
	useEffect(() => {
		(async () => {
			if (account === ethers.constants.AddressZero || !id || !ethers.utils.isAddress(id)) {
				return;
			}

			const tokenData = (
				await Moralis.Web3API.account.getTokenBalances({
					chain: 'polygon',
					address: account,
					token_addresses: ['0x2791bca1f2de4661ed88a30c99a7a9449aa84174'],
				})
			)[0];

			setUSDCBalanceFormatted(`${formatUsd(ethers.utils.formatUnits(tokenData.balance, 6))} (USDC)`);
		})();
	}, [account, id]);

	/** User chain currency balance */
	useEffect(() => {
		const balance = nativeBalance.balance
			? ethers.utils.formatEther(nativeBalance.balance).toString()
			: STRING_PLACEHOLDER;

		setBalanceFormatted(`${trimBalance(balance)} MATIC`);
	}, [nativeBalance, id]);

	const typeOptions = {
		[RESULT_TYPES.LOADING]: () => (
			<Flex
				width="100%"
				minH="100vh"
				justifyContent="center"
				alignItems="center"
			>
				<Spinner size="xl" />
			</Flex>
		),
		[RESULT_TYPES.SUCCESS]: () => {
			return (
				<Flex
					w="100%"
					m="20px 0"
					gridGap="35px"
					justifyContent="space-between"
					flexWrap="wrap"
				>
					<Flex
						gridGap="35px"
						flexWrap="wrap"
					>
						<EditAccountIcon isOwner={isOwner} />
						<MainUserInfo
							isOwner={isOwner}
							account={id}
							balance={airdropHashBalance || STRING_PLACEHOLDER}
							nativeBalance={balanceFormatted}
							USDCBalance={USDCBalanceFormatted}
						/>
						<Box display={{ base: 'block', hashup: 'none' }}>
							<InfoPane
								isMonetary
								topic={t('team.hashInWallet').toString()}
								subTopic={balanceFormatted}
								content={airdropHashBalance || STRING_PLACEHOLDER}
							/>
						</Box>
					</Flex>

					<Flex
						minW={{ base: '100%', md: '100%', hashup: '50%' }}
						alignItems="flex-end"
						alignSelf="flex-end"
						flexDirection="column"
						justifyContent="flex-end"
						maxW={{ base: '100%', md: '50%' }}
					>
						{isOwner && <EditColorThere />}
						<UserDescription isOwner={isOwner} />
					</Flex>
				</Flex>
			);
		},
		[RESULT_TYPES.NO_USER]: () => (
			<Flex
				width="100%"
				minH="100vh"
				justifyContent="center"
				alignItems="center"
			>
				{t('default.noResults').toString()}
			</Flex>
		),
	};

	const resultType = useMemo(() => {
		if (isLoading) {
			return RESULT_TYPES.LOADING;
		}
		if (!isLoading && !data) {
			return RESULT_TYPES.NO_USER;
		}
		if (data) {
			return RESULT_TYPES.SUCCESS;
		}
		return RESULT_TYPES.LOADING;
	}, [data, isLoading]);

	return (
		<Flex
			padding="24px 0 36px 0"
			m="20px 0"
			gridGap="60px"
			flexWrap="wrap"
			justifyContent="space-between"
		>
			{typeOptions[resultType]()}
		</Flex>
	);
};
